import { useStrings } from "@1js/localization-components";
import type { ButtonProps } from "@fluentui/react-components";
import {
  Spinner,
  ToggleButton,
  Tooltip,
  makeStyles,
  shorthands,
  tokens,
} from "@fluentui/react-components";
import type { FluentIconsProps } from "@fluentui/react-icons";
import { BookmarkFilled } from "@fluentui/react-icons";
import * as React from "react";
import { DeletingLabel, RemoveTooltip } from "./PromptDelete.strings";

export type PromptDeleteProps = {
  isDeleting?: boolean;
  onDelete?: () => void;
  tabIndex?: number;
};
const useStyles = makeStyles({
  deletes: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "right",
    height: "24px",
    ...shorthands.gap("4px"),
  },

  spinner: {
    ...shorthands.border("0px"),
    ...shorthands.padding("0px"),
  },

  delete: {
    ...shorthands.border("0px"),
    ...shorthands.padding("0px"),
    minWidth: "24px",
    maxWidth: "24px",
  },

  span: {
    width: "20px",
    height: "20px",
    fontSize: "20px",
  },
});

export const PromptDelete: React.FC<PromptDeleteProps> = ({
  isDeleting,
  onDelete,
  tabIndex,
}) => {
  const styles = useStyles();
  const localizations = useStrings({
    RemoveTooltip,
    DeletingLabel,
  });

  const buttonStyleProps: ButtonProps = {
    appearance: "transparent",
  };

  const iconStyleProps: FluentIconsProps = {
    style: {
      color: tokens.colorBrandForeground1,
    },
  };

  const handleOnClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    onDelete && onDelete();
  };

  return (
    <div className={styles.deletes}>
      <Tooltip content={localizations.RemoveTooltip()} relationship="label">
        <ToggleButton
          data-testid="delete-button"
          className={isDeleting ? styles.spinner : styles.delete}
          {...buttonStyleProps}
          size="small"
          onClick={handleOnClick}
          aria-busy={isDeleting}
          checked={true}
          tabIndex={tabIndex}
        >
          {isDeleting ? (
            <Spinner
              size="tiny"
              labelPosition="after"
              label={localizations.DeletingLabel()}
            />
          ) : (
            <span className={styles.span}>
              <BookmarkFilled {...iconStyleProps} />
            </span>
          )}
        </ToggleButton>
      </Tooltip>
    </div>
  );
};
